import { useContext, useState, useCallback, useEffect } from 'react';
import { MiawChatContext } from './src-contexts-MiawChatContext.js';
import { sendTextMessage } from './src-utils-miaw-api-utils.js';
import { util } from './src-utils-miaw-common.js';
import { useNRLogging } from './src-hooks-custom-hooks-useNRLogging.js';
import { CAContext } from './src-contexts-CAContext.js';
import { ChatbotContext } from './src-contexts-ChatbotContext.js';
import { getRoutingAttributes } from './src-utils-miaw-start-chat-utils.js';
import { useCustomChatEvents } from './src-hooks-custom-hooks-useCustomChatEvents.js';
import { CaseDataContext } from './src-contexts-CaseDataContext.js';
import { getQueue } from './src-utils-miaw-init-chat-utils.js';
import { useIdentity } from '@vp/auth-react';
import { getIsSendRoutingAttributes, setIsSendRoutingAttributes } from './src-utils-session-storage-utils.js';

const useMIAWChatMessage = () => {
  const { sendChatEvents } = useCustomChatEvents();
  const identity = useIdentity();
  const {
    chatState: { userInfo, customQueue }
  } = useContext(CAContext);
  const {
    setState,
    state: { conversationId, sendRoutingDataWithMessage, queueInfo }
  } = useContext(MiawChatContext);
  const {
    state: { sessionId, orderNumber, selectedProductIds }
  } = useContext(ChatbotContext);
  const {
    state: { queueType, caseData }
  } = useContext(CaseDataContext);
  const { logCustomAttributesNR } = useNRLogging();
  const [data, setData] = useState();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const [requestData, setRequestData] = useState();
  const trigger = useCallback((userMessage) => {
    setRequestData(userMessage);
  }, []);
  useEffect(() => {
    if (requestData) {
      setLoading(true);
      if (conversationId && caseData) {
        const { canonicalId } = identity;
        const sendInfo = sendRoutingDataWithMessage || getIsSendRoutingAttributes();
        logCustomAttributesNR("ca-chatbot-miaw-api-request", {
          logLevel: "info",
          message: "Sending user message",
          conversationId,
          apiType: "miawSendMessage"
        });
        sendTextMessage(
          conversationId,
          requestData,
          util.generateUUID(),
          void 0,
          sendInfo,
          sendInfo ? getRoutingAttributes(
            { chatbotIdentifier: sessionId, orderNumber, selectedProductIds, userShopperId: canonicalId },
            caseData,
            // Pass caseData to getRoutingAttributes
            getQueue(queueType, queueInfo),
            customQueue || queueType,
            userInfo?.userData
          ) : void 0
        ).then((res) => {
          setState((prev) => ({
            ...prev,
            sendRoutingDataWithMessage: false
            // Reset the flag after sending the message
          }));
          setIsSendRoutingAttributes(false);
          setData({
            id: res.conversationEntries[0].id,
            messageContent: requestData,
            senderDisplayName: "User",
            timestamp: res.conversationEntries[0].clientTimestamp,
            type: "user",
            showFeedback: false
          });
          setError(void 0);
          sendChatEvents("onChatMessage", {});
          logCustomAttributesNR("ca-chatbot-miaw-api-response", {
            logLevel: "info",
            message: "User message sent successfully",
            conversationId,
            apiType: "miawSendMessage"
          });
        }).catch((error2) => {
          setError(error2);
          logCustomAttributesNR("ca-chatbot-miaw-api-error", {
            errorName: error2.name,
            errorStack: error2.stack,
            message: error2.message,
            logLevel: "error",
            apiType: "miawSendMessage"
          });
        }).finally(() => {
          setLoading(false);
          setRequestData(void 0);
        });
      }
    }
  }, [conversationId, requestData, userInfo]);
  return {
    data,
    loading,
    error,
    trigger
  };
};

export { useMIAWChatMessage };
//# sourceMappingURL=src-hooks-miaw-useMIAWChatMessage.js.map
